/**
 * StatusBadgeAtom.tsx
 *
 * A small badge component for displaying status.
 * Three mutually exclusive APIs:
 *   Sphere variant: pass `status` (existing consumers unchanged).
 *   Marketplace tone variant: pass `tone` + `label` + optional `icon`.
 *   Roster variant: pass `variant` + `label` (+ `statusValue` for `measured`); each variant has its own
 *   marker shape so the state never rests on colour alone.
 */

import React, { useId } from 'react';
import { Tooltip } from 'primereact/tooltip';
import { getIconClass } from '../../../utils/statusColors';
import './StatusBadgeAtom.scss';

export type StatusBadgeStatus = 'active' | 'overdue' | 'draft' | 'error' | 'unknown' | 'pending' | 'deactivated'
  | 'approach' | 'channel' | 'manual' | 'fork' | 'observing' | 'paused'
  | 'conformance_pass' | 'conformance_fail' | 'conformance_stale';

export type StatusBadgeTone = 'success' | 'warn' | 'danger' | 'info' | 'neutral';

/**
 * The one mapping from a reported severity to the tone that reports it.
 *
 * Every surface that shows a severity — a schema's business rules, the Studio Desk's
 * needs-attention findings, conformance output — must colour `error`/`warning`/`info`
 * identically, or the same condition reads as two different conditions in two places. Lives
 * here because this is the module that owns `StatusBadgeTone`, and because a private copy per
 * consumer is how the three drift apart one `warning` at a time.
 *
 * Keyed by the shared severity union (`types/businessRules`, `types/studio`), which both
 * spell `'error' | 'warning' | 'info'`.
 */
export const SEVERITY_TONE: Record<'error' | 'warning' | 'info', StatusBadgeTone> = {
  error: 'danger',
  warning: 'warn',
  info: 'info',
};

/** How a rollup roster row's status reads; each variant draws a different marker shape. */
export type StatusBadgeRosterVariant = 'measured' | 'not-yet-measured' | 'hidden' | 'broken';

export type StatusBadgeSize = 'small' | 'medium';

interface StatusBadgeBaseProps {
  size?: StatusBadgeSize;
  className?: string;
  'data-testid'?: string;
  'data-datum'?: string;
  'data-kind'?: string;
}

// Sphere/system status variant (existing API — unchanged)
interface StatusBadgeSphereVariantProps extends StatusBadgeBaseProps {
  status: StatusBadgeStatus;
  label?: string;
  showIcon?: boolean;
  tone?: never;
  icon?: never;
  variant?: never;
}

// Rollup roster variant: a status marker (dot / hatched ring / hidden icon / broken icon) plus label
interface StatusBadgeRosterVariantProps extends StatusBadgeBaseProps {
  variant: StatusBadgeRosterVariant;
  label: string;
  /** `StatusValue` (underscored) of a `measured` row; picks the `--status-*` colour of the dot. */
  statusValue?: string;
  tooltip?: string;
  status?: never;
  tone?: never;
  icon?: never;
  showIcon?: never;
}

// Marketplace tone variant (new additive API)
interface StatusBadgeToneVariantProps extends StatusBadgeBaseProps {
  tone: StatusBadgeTone;
  label: string;
  icon?: string;
  /** Optional tooltip text. When set, renders a PrimeReact Tooltip targeting this badge. */
  tooltip?: string;
  status?: never;
  showIcon?: never;
  variant?: never;
}

export type StatusBadgeAtomProps =
  | StatusBadgeSphereVariantProps
  | StatusBadgeToneVariantProps
  | StatusBadgeRosterVariantProps;

/**
 * Get default label for status
 */
const getDefaultLabel = (status: StatusBadgeStatus): string => {
  switch (status) {
    case 'active':
      return 'Active';
    case 'overdue':
      return 'Overdue';
    case 'draft':
      return 'Draft';
    case 'error':
      return 'Error';
    case 'pending':
      return 'Pending';
    case 'deactivated':
      return 'Deactivated';
    case 'observing':
      return 'Observing';
    case 'paused':
      return 'Paused';
    case 'approach':
    case 'channel':
    case 'manual':
    case 'fork':
    case 'conformance_pass':
    case 'conformance_fail':
    case 'conformance_stale':
      // Metaspace source statuses (and conformance statuses) must always be rendered
      // with a translated `label` supplied by the caller. Returning empty here keeps
      // the atom locale-neutral.
      return '';
    case 'unknown':
    default:
      return 'Unknown';
  }
};

const StatusBadgeAtom: React.FC<StatusBadgeAtomProps> = (props) => {
  const { size = 'small', className, 'data-testid': testId, 'data-datum': dataDatum, 'data-kind': dataKind } = props;
  // useId produces values with colons (`:r0:`) which are invalid in CSS selectors;
  // strip them for use as a class-based tooltip target. Called unconditionally
  // to satisfy Rules of Hooks; only used when the tone variant supplies `tooltip`.
  const rawId = useId();

  if (props.variant !== undefined) {
    const { variant, label, statusValue, tooltip } = props;
    const classes = ['status-badge', 'status-badge--roster', `status-badge--roster-${variant}`, `status-badge--${size}`, className]
      .filter(Boolean)
      .join(' ');
    let marker: React.ReactNode;
    if (variant === 'measured') {
      const tone = (statusValue ?? 'unknown').replaceAll('_', '-');
      marker = <span className={`status-badge__marker status-badge__marker--dot status-badge__marker--${tone}`} aria-hidden="true" />;
    } else if (variant === 'not-yet-measured') {
      marker = <span className="status-badge__marker status-badge__marker--hatched" aria-hidden="true" />;
    } else if (variant === 'hidden') {
      marker = <i className="status-badge__marker pi pi-eye-slash" aria-hidden="true" />;
    } else {
      marker = <i className="status-badge__marker pi pi-exclamation-triangle" aria-hidden="true" />;
    }
    return (
      <span className={classes} data-testid={testId} data-datum={dataDatum} data-kind={dataKind} data-variant={variant} title={tooltip}>
        {marker}
        <span className="status-badge__label">{label}</span>
      </span>
    );
  }

  if ('tone' in props && props.tone !== undefined) {
    const { tone, label, icon, tooltip } = props;
    const tooltipTargetClass = tooltip
      ? `status-badge-tooltip-${rawId.replaceAll(':', '')}`
      : null;
    const classes = [
      'status-badge',
      `status-badge--tone-${tone}`,
      `status-badge--${size}`,
      tooltipTargetClass,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const badge = (
      <span
        className={classes}
        data-testid={testId}
        data-datum={dataDatum}
        data-kind={dataKind}
        {...(tooltip
          ? {
              'data-pr-tooltip': tooltip,
              'data-pr-position': 'top',
              'aria-label': tooltip,
              role: 'status',
            }
          : {})}
      >
        {icon && <i className={icon} aria-hidden="true" />}
        <span className="status-badge__label">{label}</span>
      </span>
    );

    if (tooltip && tooltipTargetClass) {
      return (
        <>
          {badge}
          <Tooltip target={`.${tooltipTargetClass}`} />
        </>
      );
    }
    return badge;
  }

  const { status, label, showIcon = false } = props;
  const displayLabel = label ?? getDefaultLabel(status);
  const classes = [
    'status-badge',
    `status-badge--${status}`,
    `status-badge--${size}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classes} data-testid={testId} data-datum={dataDatum} data-kind={dataKind}>
      {showIcon && <i className={getIconClass(status)} aria-hidden="true" />}
      <span className="status-badge__label">{displayLabel}</span>
    </span>
  );
};

StatusBadgeAtom.displayName = 'StatusBadgeAtom';

export default StatusBadgeAtom;
